import React, { useEffect, useState } from 'react';
import './Invoices.scss';
import SideNav from '../../components/shared/sidenav/SideNav';
import { IInvoice, IInvoiceItem } from '../../models/Invoice';
import ApiService from '../../services/apiService';
import { toast, ToastContainer } from 'react-toastify';
import { toastOptions } from '../../utils/toast';
import URL from '../../urls/Urls';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const Invoices: React.FC = () => {
  const [newInvoice, setNewInvoice] = useState<IInvoice>({
    invoice_number: '',
    pod: [],
    track: '',
    delivered_date: '',
  });
  const [search, setSearch] = useState('');
  const [invoices, setInvoices] = useState<IInvoiceItem[]>([]);
  const [startDateOpen, setStartDateOpen] = useState<boolean>(false);

  useEffect(() => {
    getTransitInvoices();
  }, []);

  const getTransitInvoices = async () => {
    try {
      const res = await ApiService().client.get(
        URL.DELIVERY_DETAILS.FETCH_TRANSIT_INVOICES
      );
      if (res.data) {
        setInvoices(res.data.invoices);
      }
    } catch (error) {
      console.error(error);
      toast.error('Invoice Fetching failed', toastOptions);
    }
  };

  const getInvoiceDetails = async () => {
    if (search === '') {
      getTransitInvoices();
    } else {
      try {
        const res = await ApiService().client.get(
          URL.DELIVERY_DETAILS.UPDATE_INVOICE(search)
        );
        if (res.data) {
          setInvoices([res.data.details]);
        }
      } catch (error) {
        console.error(error);
        toast.error('Invoice Fetching failed', toastOptions);
      }
    }
  };

  const updateInvoice = (
    key: 'invoice_number' | 'track' | 'delivered_date',
    value: any
  ) => {
    setNewInvoice((old) => {
      return { ...old, [key]: value };
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const files = Array.from(event.target.files);
      setNewInvoice((old) => {
        return { ...old, pod: files };
      });
    }
  };

  const handleSave = async () => {
    const formData = new FormData();
    formData.append('pod', newInvoice.pod[0]);
    formData.append('track', newInvoice.track);
    formData.append('delivered_date', newInvoice.delivered_date);

    try {
      const res = await ApiService().client.put(
        URL.DELIVERY_DETAILS.UPDATE_INVOICE(newInvoice.invoice_number),
        formData
      );
      if (res.data) {
        toast.success('Field update succesful', toastOptions);
        clearInvoice();
      }
    } catch (error) {
      console.error(error);
      toast.error('Upload failed. Please try again.', toastOptions);
    }
  };

  const clearInvoice = () => {
    setNewInvoice({
      invoice_number: '',
      pod: [],
      track: '',
      delivered_date: '',
    });
  };

  const toggleStartDate = () => {
    setStartDateOpen((val) => !val);
  };

  const startDateSelected = (event: any) => {
    console.log('NEW DATE: ', dayjs(event.toDate()).format('DD-MM-YYYY'));
    updateInvoice('delivered_date', dayjs(event.toDate()).format('DD-MM-YYYY'));
    toggleStartDate();
  };

  const onEnterPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      getInvoiceDetails();
    }
  };

  return (
    <div className="flex-row full-width full-height">
      <SideNav></SideNav>
      <div className="invoice-main-page flex-column flex-1 px-16 py-22">
        <div className="invoice-list-container p-16 flex-column gap-32">
          <div className="invoice-update-container p-16 flex-column gap-24">
            <div className="flex-row gap-12">
              <div className="flex-column gap-8 flex-1">
                <p className="xetgo-font-tag" style={{ color: '#484848' }}>
                  Invoice Number
                </p>
                <input
                  type="text"
                  className="xetgo-font-tag px-8 py-10 invoice-input"
                  value={newInvoice.invoice_number}
                  onChange={(e) =>
                    updateInvoice('invoice_number', e.target.value)
                  }
                />
              </div>
              <div className="flex-column gap-8 flex-1">
                <p className="xetgo-font-tag" style={{ color: '#484848' }}>
                  POD
                </p>
                <input
                  type="file"
                  id="pod-file"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <div className="xetgo-font-tag p-4 invoice-input flex-row gap-6 align-items-center">
                  <label htmlFor="pod-file" className="select-file-label">
                    <p
                      className="xetgo-font-tag px-12 py-6 bold"
                      style={{
                        color: '#333333CC',
                        borderRadius: '6px',
                        background: '#33333329',
                        width: 'fit-content',
                      }}
                    >
                      Choose File
                    </p>
                  </label>
                  {newInvoice.pod.length > 0 && (
                    <p className="xetgo-font-tag">{newInvoice.pod[0].name}</p>
                  )}
                </div>
              </div>
              <div className="flex-column gap-8 flex-1">
                <p className="xetgo-font-tag" style={{ color: '#484848' }}>
                  Delivered Date
                </p>
                <div className="xetgo-font-tag flex-row align-items-center justify-content-space-between gap-12 invoice-input px-8 py-10">
                  <p className="xetgo-font-tag">{newInvoice.delivered_date}</p>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={dayjs(newInvoice.delivered_date).format(
                        'DD-MM-YYYY'
                      )}
                      format={'DD-MM-YYYY'}
                      views={['year', 'month', 'day']}
                      onChange={startDateSelected}
                      open={startDateOpen}
                      className="delivery-date-selector"
                    />
                  </LocalizationProvider>
                  <img
                    src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20240107114755-calendar-range.svg"
                    onClick={toggleStartDate}
                    className="cursor-pointer"
                    height={16}
                    width={16}
                  />
                </div>
              </div>
            </div>
            <div className="flex-column gap-8 full-width">
              <p className="xetgo-font-tag" style={{ color: '#484848' }}>
                Delivery Details
              </p>
              <textarea
                className="xetgo-font-tag px-8 py-6 invoice-input"
                rows={3}
                value={newInvoice.track}
                onChange={(e) => updateInvoice('track', e.target.value)}
              />
            </div>
            <div className="flex-row justify-content-flex-end">
              <p
                className="xetgo-font-tag py-4 px-20 cursor-pointer"
                style={{
                  background:
                    newInvoice.invoice_number === '' ? '#D3D3D3' : '#5151EC',
                  color: '#FFFFFF',
                  borderRadius: '4px',
                }}
                onClick={handleSave}
              >
                Save
              </p>
              <p
                className="xetgo-font-tag py-4 px-20 cursor-pointer"
                style={{
                  color: '#5151EC',
                }}
                onClick={clearInvoice}
              >
                Clear
              </p>
            </div>
          </div>
          <div className="invoice-track-list-header flex-column gap-24">
            <div className="flex-row">
              <div className="search-container p-8 flex-row align-items-center gap-8">
                <input
                  className="search-box flex-1 xetgo-font-tag"
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={onEnterPress}
                  placeholder="Search Invoice Number"
                ></input>
                <img
                  src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20240821094925-search.svg"
                  alt="search"
                  width={16}
                  height={16}
                  className="cursor-pointer"
                  onClick={getInvoiceDetails}
                />
              </div>
              {/* <div className="ongoing-filter flex-row">
                <img src="" alt="filter" />
                <p>Ongoing Deliveries</p>
              </div> */}
            </div>
            <div className="invoice-table-container">
              <div className="invoice-table-grid invoice-table-header">
                <p
                  className="xetgo-font-tag header-item"
                  style={{ color: '#6B7280' }}
                >
                  Company Name
                </p>
                <p
                  className="xetgo-font-tag header-item"
                  style={{ color: '#6B7280' }}
                >
                  PO
                </p>
                <p
                  className="xetgo-font-tag header-item"
                  style={{ color: '#6B7280' }}
                >
                  Invoice No
                </p>
                <p
                  className="xetgo-font-tag header-item"
                  style={{ color: '#6B7280' }}
                >
                  Tracking Details
                </p>
                <p
                  className="xetgo-font-tag header-item"
                  style={{ color: '#6B7280' }}
                >
                  Delivered Date
                </p>
                <p
                  className="xetgo-font-tag header-item"
                  style={{ color: '#6B7280' }}
                >
                  Links
                </p>
                <p
                  className="xetgo-font-tag header-item"
                  style={{ color: '#6B7280' }}
                >
                  Uploaded On
                </p>
              </div>
              {invoices.map((invoice) => (
                <div
                  className="invoice-table-grid invoice-table-body"
                  key={invoice.invoice_no}
                >
                  <p className="xetgo-font-tag entry-item">{invoice.client}</p>
                  <p className="xetgo-font-tag entry-item">
                    {invoice.link ? (
                      <a
                        href={invoice.link}
                        target="_blank"
                        rel="noreferrer"
                        className="xetgo-font-tag"
                      >
                        {invoice.po_number}
                      </a>
                    ) : (
                      <p className="xetgo-font-tag">{invoice.po_number}</p>
                    )}
                  </p>
                  <p className="xetgo-font-tag entry-item">
                    {invoice.invoice_no}
                  </p>
                  <p className="xetgo-font-tag entry-item">
                    {invoice.tracking}
                  </p>
                  <p className="xetgo-font-tag entry-item">
                    {invoice.delivered_date}
                  </p>
                  <div className="flex-row gap-6 align-items-center entry-item gap-6">
                    {invoice.pod && invoice.pod !== '-' ? (
                      <a
                        href={invoice.pod}
                        target="_blank"
                        rel="noreferrer"
                        className="xetgo-font-tag"
                      >
                        POD
                      </a>
                    ) : (
                      <p className="xetgo-font-tag">-</p>
                    )}
                    <p className="xetgo-font-tag">|</p>
                    {invoice.invoice_url ? (
                      <a
                        href={invoice.invoice_url}
                        target="_blank"
                        rel="noreferrer"
                        className="xetgo-font-tag"
                      >
                        Invoice
                      </a>
                    ) : (
                      <p className="xetgo-font-tag">-</p>
                    )}
                  </div>
                  <p className="xetgo-font-tag entry-item">
                    {invoice.created_at}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" />
    </div>
  );
};

export default Invoices;
